import type { WebhookIntegrationCreateFieldFormValues } from '@modules/integrations/lib/types';
import type { WebhookIntegrationData } from '@shared/api';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { DraftIntegrationField } from '../lib/types';

const normalizeStringValue = (value: string | null | undefined) => value?.trim() ?? '';

const areFieldsEqual = (left: DraftIntegrationField, right: DraftIntegrationField) => {
  return (
    left.location === right.location &&
    normalizeStringValue(left.parameterName) === normalizeStringValue(right.parameterName) &&
    left.agentFieldId === right.agentFieldId &&
    normalizeStringValue(left.value) === normalizeStringValue(right.value)
  );
};

type UseIntegrationFieldsDraftParams = {
  integrationId: number;
  integrationInfo?: WebhookIntegrationData;
  createIntegrationField: (
    integrationId: number,
    values: WebhookIntegrationCreateFieldFormValues,
  ) => Promise<boolean>;
  deleteIntegrationField: (integrationFieldId: number) => Promise<void>;
};

export const useIntegrationFieldsDraft = ({
  integrationId,
  integrationInfo,
  createIntegrationField,
  deleteIntegrationField,
}: UseIntegrationFieldsDraftParams) => {
  const [draftFields, setDraftFields] = useState<DraftIntegrationField[]>([]);
  const draftFieldsRef = useRef<DraftIntegrationField[]>([]);
  const [busyLocalIds, setBusyLocalIds] = useState<string[]>([]);
  const newRowCounterRef = useRef(0);

  useEffect(() => {
    draftFieldsRef.current = draftFields;
  }, [draftFields]);

  useEffect(() => {
    if (!integrationInfo) return;

    const persistedFields = integrationInfo.fields.map((field) => ({
      localId: `id:${field.id}`,
      id: field.id,
      location: field.location,
      parameterName: field.parameter_name,
      agentFieldId: field.agent_field_id,
      value: field.value,
    }));

    setDraftFields((prev) => {
      const tempFields = prev.filter((field) => !field.id);
      const tempFieldsWithoutSaved = tempFields.filter(
        (tempField) =>
          !persistedFields.some((persistedField) => areFieldsEqual(tempField, persistedField)),
      );

      return [...persistedFields, ...tempFieldsWithoutSaved];
    });

    setBusyLocalIds([]);
  }, [integrationInfo]);

  const setRowBusy = useCallback((localId: string, nextBusy: boolean) => {
    setBusyLocalIds((prev) => {
      const isAlreadyBusy = prev.includes(localId);
      if (nextBusy && isAlreadyBusy) return prev;
      if (!nextBusy && !isAlreadyBusy) return prev;
      return nextBusy ? [...prev, localId] : prev.filter((id) => id !== localId);
    });
  }, []);

  const updateRow = useCallback((localId: string, patch: Partial<DraftIntegrationField>) => {
    setDraftFields((prev) =>
      prev.map((row) => (row.localId === localId ? { ...row, ...patch } : row)),
    );
  }, []);

  const addRow = useCallback(() => {
    newRowCounterRef.current += 1;
    const localId = `tmp:${Date.now()}:${newRowCounterRef.current}`;

    setDraftFields((prev) => [
      ...prev,
      {
        localId,
        location: 'query',
        parameterName: '',
        agentFieldId: null,
        value: null,
      },
    ]);
  }, []);

  const saveRow = useCallback(
    async (localId: string) => {
      const row = draftFieldsRef.current.find((field) => field.localId === localId);
      if (!row) return;

      if (row.id) return;

      const parameterName = row.parameterName.trim();
      if (!parameterName) return;
      const trimmedValue = row.value?.trim() ?? '';
      if (row.agentFieldId === null && !trimmedValue) return;

      setRowBusy(localId, true);
      try {
        const isCreated = await createIntegrationField(integrationId, {
          location: row.location,
          parameterName,
          agentFieldId: row.agentFieldId,
          value: trimmedValue ? trimmedValue : null,
        });

        if (!isCreated) return;

        // Remove just-saved draft row; persisted version comes from RTK Query cache update.
        setDraftFields((prev) => prev.filter((field) => field.localId !== localId));
      } finally {
        setRowBusy(localId, false);
      }
    },
    [createIntegrationField, integrationId, setRowBusy],
  );

  const deleteRow = useCallback(
    async (localId: string) => {
      const row = draftFieldsRef.current.find((field) => field.localId === localId);
      if (!row) return;

      if (!row.id) {
        setDraftFields((prev) => prev.filter((field) => field.localId !== localId));
        return;
      }

      setRowBusy(localId, true);
      try {
        await deleteIntegrationField(row.id);
      } finally {
        setRowBusy(localId, false);
      }
    },
    [deleteIntegrationField, setRowBusy],
  );

  return {
    draftFields,
    busyLocalIds,
    updateRow,
    addRow,
    saveRow,
    deleteRow,
  };
};
